import EntityService from './EntityService'
import { Invoice, INVOICE_STATUS, invoiceConverter, InvoicePayment, invoicePaymentConverter } from '@/models/invoice'
import { collection, deleteDoc, doc, getDocs, setDoc } from 'firebase/firestore'
import FirebaseService from './FirebaseService'
import { toNumber } from '@/utils'

export default class InvoiceService extends EntityService<Invoice> {
  constructor() {
    super({ collectionName: 'invoices', converter: invoiceConverter })
  }

  /**
   * Find invoices by status
   *
   * @param status
   * @returns Promise<Invoice[]>
   */
  public async find_by_status(status: string) {
    if (!Object.values(INVOICE_STATUS).includes(status)) {
      throw new Error('Invalid status')
    }

    return await this.find_by('status', '==', status)
  }

  /**
   * Find invoices by with created within the date range
   *
   * @param string dateRange (e.g. '7d,30d,90d,365d')
   * @returns Promise<Invoice[]>
   */
  public async find_by_created_within(dateRange: string) {
    const value = dateRange.split('d')[0]
    const date = new Date()
    date.setDate(date.getDate() - parseInt(value))

    return await this.find_by('created_at', '>=', date.toISOString())
  }

  public async getPreviouslyInvoicedValue (projectId: string, currentInvoice: string=''): Promise<any> {
    const previousInvoices = await this.find_by('projectId', '==', projectId)
    const previousInvoiceDetails = {
      previouslyInvoicedValue: 0,
      previouslyInvoicedCredit: 0,
    }

    previousInvoices.forEach(invoice => {
      if (invoice.id === currentInvoice) return
      previousInvoiceDetails.previouslyInvoicedValue += Math.abs(toNumber(invoice.total) - toNumber(invoice.credit))
      previousInvoiceDetails.previouslyInvoicedCredit += toNumber(invoice.credit)
    })

    return previousInvoiceDetails
  }

  public async deleteBudgetInvoices (projectId: string): Promise<boolean> {
    const budgetInvoices = await this.find_by('projectId', '==', projectId)
    const allPromises = []
    budgetInvoices.forEach(invoice => {
      allPromises.push(this.forceDelete(invoice.id))
    })

    await Promise.all(allPromises)
    return true
  }

  async getPayments(invoiceId: string) {
    const db = await FirebaseService.firestore()
    const invoiceCollection = (await this.getCollection()).path
    const collectionRef = collection(db, `${invoiceCollection}/${invoiceId}/payments`).withConverter(invoicePaymentConverter)
    const docs = await getDocs(collectionRef)

    if (docs.empty) return []
    return docs.docs.map((doc) => ({ ...doc.data(), id: doc.id }) as InvoicePayment)
  }

  async addPayment(invoiceId: string, payment: any) {
    const db = await FirebaseService.firestore()
    const invoiceCollection = (await this.getCollection()).path
    const collectionRef = collection(db, `${invoiceCollection}/${invoiceId}/payments`)
    const docRef = doc(collectionRef)
    payment.id = docRef.id
    await setDoc(docRef, payment)

    return payment
  }

  async deletePayment(invoiceId: string, paymentId: string) {
    const db = await FirebaseService.firestore()
    const invoiceCollection = (await this.getCollection()).path
    const collectionRef = collection(db, `${invoiceCollection}/${invoiceId}/payments`)
    const docRef = doc(collectionRef, paymentId)
    await deleteDoc(docRef)
    return true
  }

  async deleteInvoice (id: string): Promise<boolean> {
    // delete all payments
    const payments = await this.getPayments(id)
    const allPromises = payments.map((payment) => this.deletePayment(id, payment.id))
    await Promise.all(allPromises)
    return await this.forceDelete(id)
  }
}
